import React, { useState } from 'react';
import theme from './SortableImageUpload.module.scss';
import { max } from 'lodash-es';
import { ConfirmRemoveButton } from '@/components/Buttons';
import { Trans } from '@lingui/react/macro';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { getUniqueId } from '@/utils/html';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { FileUpload, Sortable, Thumbnail, arrayMove } from '@ygtechnologies/bygr-frontend-component-library';
import { LightBox } from '@ygtechnologies/rubus-ui';

interface ImageLike {
    id?: number;
    sortOrder: number;
    fileData?: FileData;
    _internalId?: string;
    name?: string;
}

interface SortableImageProps {
    image: ImageLike;
    handleProps: object;
    onImageClick(): void;
    onDeleteClick(): void;
    getThumbnailUrl(id: number): string;
}
const SortableImage: React.FC<SortableImageProps> = ({
    image,
    onImageClick,
    onDeleteClick,
    handleProps,
    getThumbnailUrl,
}) => (
    <div className={theme.imageContainer}>
        <div className={theme.imageWrapper} onClick={image.id ? onImageClick : undefined} {...handleProps}>
            <Thumbnail draggable={false} src={image.id ? getThumbnailUrl(image.id) : image.fileData} />
        </div>
        <span className={theme.imageButtons}>
            <ConfirmRemoveButton
                dialogTitle={<Trans>Delete image</Trans>}
                dialogBody={<Trans>Are you sure you want to delete this image?</Trans>}
                confirmButtonText={<Trans>Delete</Trans>}
                onConfirm={onDeleteClick}
                iconPosition="left"
                icon={faTrashAlt}
                color="link">
                <Trans>Delete</Trans>
            </ConfirmRemoveButton>
        </span>
    </div>
);

export interface SortableImageUploadProps {
    readonly?: boolean;
    onImagesChange(newValue: ImageLike[]): void;
    files: ImageLike[];
    getThumbnailUrl(id: number): string;
    getUrl(id: number): string;
}

export const SortableImageUpload: React.FC<SortableImageUploadProps> = ({
    readonly = false,
    onImagesChange,
    files,
    getThumbnailUrl,
    getUrl,
}) => {
    const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null);

    const handleFileUpload = async (newFiles: File[]) => {
        if (readonly) {
            return;
        }

        let maxSortNr = max(files.map((i) => i.sortOrder)) || 0;

        const addedFiles = newFiles.map((file, i) => {
            const sortOrder = maxSortNr + Math.abs(i) + 1;

            return { _internalId: getUniqueId(), sortOrder, data: file };
        });

        onImagesChange([
            ...files,
            ...addedFiles.map((x) => ({
                _internalId: x._internalId,
                sortOrder: x.sortOrder,
                fileData: new FileData(x.data),
                name: x.data.name,
            })),
        ]);
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const newArray = arrayMove(files, oldIndex, newIndex);
        onImagesChange(newArray);
    };
    const handleDelete = (image: ImageLike) => {
        onImagesChange(files.filter((i) => i !== image));
    };
    const handleImageClick = (image: ImageLike) => {
        const index = files.indexOf(image);

        if (index > -1) {
            setActiveImageIndex(files.indexOf(image));
        }
    };

    return (
        <div>
            <Sortable
                className={theme.imageListContainer}
                items={files}
                idField={(x) => x.id || x._internalId}
                container={({ children, className }) => (
                    <div className={className}>
                        {children}
                        {!readonly && (
                            <FileUpload fileType="image" containerClassName={theme.dropButton} onDrop={handleFileUpload} />
                        )}
                    </div>
                )}
                onSortEnd={onSortEnd}
                direction="both">
                {(img, { handleProps, isDragging }) => (
                    <SortableImage
                        getThumbnailUrl={getThumbnailUrl}
                        handleProps={handleProps}
                        onDeleteClick={() => handleDelete(img)}
                        onImageClick={() => handleImageClick(img)}
                        key={`item-${img.id || img._internalId}`}
                        image={img}
                    />
                )}
            </Sortable>

            <LightBox
                activeIndex={activeImageIndex}
                setIndex={setActiveImageIndex}
                images={files.map((i) => ({
                    src: getUrl(i.id!),
                    thumbnailSrc: getThumbnailUrl(i.id!),
                    type: 'image',
                }))}
            />
        </div>
    );
};
